"use client";

import { useSyncExternalStore } from "react";
import { X, Play } from "lucide-react";
import Link from "next/link";

interface AnnouncementBarProps {
  enabled?: boolean;
  message?: string;
  linkUrl?: string;
  linkText?: string;
  bgColor?: string;
  textColor?: string;
  marquee?: boolean;
}

const STORAGE_KEY = "announcement-bar-closed-v2";

// Kapatma yalnızca mevcut tarayıcı oturumu için geçerli (sessionStorage):
// tarayıcı/sekme kapanıp yeniden açılınca çubuk tekrar görünür.
function subscribe(callback: () => void) {
  const handler = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) callback();
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}

function getSnapshot() {
  return sessionStorage.getItem(STORAGE_KEY);
}

function getServerSnapshot(): string | null {
  return null;
}

export function AnnouncementBar({
  enabled = true,
  message,
  linkUrl,
  linkText,
  bgColor = "var(--sf-accent)",
  textColor = "#ffffff",
  marquee = false,
}: AnnouncementBarProps) {
  const closed = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const isVisible = enabled && !!message && !closed;

  const handleClose = () => {
    sessionStorage.setItem(STORAGE_KEY, "1");
    // Aynı tab'da storage event fire olmaz, manuel dispatch
    window.dispatchEvent(new StorageEvent("storage", { key: STORAGE_KEY }));
  };

  if (!isVisible) return null;

  return (
    <div
      className="relative flex items-center justify-center overflow-hidden px-10 py-1.5 text-[11px] font-semibold sm:px-12 sm:py-2.5 sm:text-sm"
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      {marquee ? (
        <div className="flex overflow-hidden w-full">
          <div className="flex animate-marquee whitespace-nowrap">
            {[...Array(6)].map((_, i) => (
              <span key={i} className="flex items-center gap-2 mx-8">
                <span>{message}</span>
                {linkUrl && (
                  <Link
                    href={linkUrl}
                    className="group underline hover:opacity-80 flex items-center gap-1 shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {linkText || "İncele"}
                    <Play className="h-2 w-2 fill-current stroke-0 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                )}
              </span>
            ))}
          </div>
        </div>
      ) : (
        <span className="flex items-center gap-2 text-center">
          <span className="truncate">{message}</span>
          {linkUrl && (
            <Link
              href={linkUrl}
              className="group underline hover:opacity-80 flex items-center gap-1 shrink-0"
            >
              {linkText || "İncele"}
              <Play className="h-2 w-2 fill-current stroke-0 transition-transform group-hover:translate-x-0.5" />
            </Link>
          )}
        </span>
      )}
      <button
        onClick={handleClose}
        className="absolute right-2 p-2 hover:opacity-70 transition-opacity min-w-[44px] min-h-[44px] flex items-center justify-center"
        aria-label="Kapat"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
